import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Step } from "@/components/sequences/types";
import { useSalesBlink } from "@/hooks/useSalesBlink";

export const useSequenceOperations = () => {
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { startSequence } = useSalesBlink();

  const handleDelete = async (id: string) => {
    console.log('Starting sequence deletion for ID:', id);
    
    if (!id) {
      console.error("No sequence ID provided for deletion");
      toast({
        title: "Error",
        description: "Invalid sequence ID",
        variant: "destructive",
      });
      return;
    }

    try {
      // First delete all enrollments associated with this sequence
      console.log('Deleting sequence enrollments...');
      const { error: enrollmentsError } = await supabase
        .from('sequence_enrollments')
        .delete()
        .eq('sequence_id', id);

      if (enrollmentsError) {
        console.error("Failed to delete sequence enrollments:", enrollmentsError);
        throw new Error(`Failed to delete enrollments: ${enrollmentsError.message}`);
      }

      // Then delete all steps associated with this sequence
      console.log('Deleting sequence steps...');
      const { error: stepsError } = await supabase
        .from('sequence_steps')
        .delete()
        .eq('sequence_id', id);

      if (stepsError) {
        console.error("Failed to delete sequence steps:", stepsError);
        throw new Error(`Failed to delete steps: ${stepsError.message}`);
      }

      // Finally delete the sequence itself
      console.log('Deleting sequence template...');
      const { error: sequenceError } = await supabase
        .from('sequence_templates')
        .delete()
        .eq('id', id);

      if (sequenceError) {
        console.error("Failed to delete sequence:", sequenceError);
        throw new Error(`Failed to delete sequence: ${sequenceError.message}`);
      }

      console.log('Sequence deletion completed successfully');
      toast({
        title: "Success",
        description: "Sequence deleted successfully",
      });

    } catch (error) {
      console.error("Error in handleDelete:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete sequence",
        variant: "destructive",
      });
      throw error;
    }
  };

  const createSequenceTemplate = async (name: string, description: string) => {
    console.log('Creating sequence template:', { name, description });
    try {
      const { data, error } = await supabase
        .from("sequence_templates")
        .insert({
          name,
          description,
          trigger_type: "manual",
        })
        .select()
        .single();

      if (error) {
        console.error("Sequence template creation error:", error);
        throw new Error(`Failed to create sequence: ${error.message}`);
      }

      console.log('Sequence template created:', data);
      return data;
    } catch (error) {
      console.error("Error in createSequenceTemplate:", error);
      throw error;
    }
  };

  const createSequenceSteps = async (sequenceId: string, steps: Step[]) => {
    console.log('Creating sequence steps:', { sequenceId, steps });
    try {
      const stepsToInsert = steps.map((step) => ({
        sequence_id: sequenceId,
        step_number: step.step_number,
        channel: step.channel,
        content_template: step.content_template,
        delay_hours: step.delay_hours,
      }));

      const { error } = await supabase
        .from("sequence_steps")
        .insert(stepsToInsert);

      if (error) {
        console.error("Steps insertion error:", error);
        throw new Error(`Failed to create steps: ${error.message}`);
      }

      console.log('Sequence steps created successfully');
    } catch (error) {
      console.error("Error in createSequenceSteps:", error);
      throw error;
    }
  };

  const createEnrollments = async (sequenceId: string, leadIds: string[]) => {
    console.log('Creating enrollments:', { sequenceId, leadIds });
    try {
      const enrollments = leadIds.map(leadId => ({
        sequence_id: sequenceId,
        lead_id: leadId,
        status: 'active',
        current_step: 1
      }));

      const { error } = await supabase
        .from("sequence_enrollments")
        .insert(enrollments);

      if (error) {
        console.error("Enrollment creation error:", error);
        throw new Error(`Failed to create enrollments: ${error.message}`);
      }

      console.log('Enrollments created successfully');
    } catch (error) {
      console.error("Error in createEnrollments:", error);
      throw error;
    }
  };

  const saveSequence = async (
    name: string,
    description: string,
    steps: Step[],
    selectedLeads: string[]
  ) => {
    if (isSaving) return;
    
    console.log('Starting sequence save:', { name, description, steps, selectedLeads });
    setIsSaving(true);
    
    try {
      // Step 1: Create sequence template
      const sequence = await createSequenceTemplate(name, description);
      console.log('Sequence template created:', sequence);
      
      // Step 2: Create sequence steps
      await createSequenceSteps(sequence.id, steps);
      
      // Step 3: Handle lead enrollments if any
      if (selectedLeads.length > 0) {
        await createEnrollments(sequence.id, selectedLeads);
        
        // Step 4: Start the sequence if there are leads
        try {
          await startSequence({
            sequence_id: sequence.id,
            leads: selectedLeads,
            steps: steps,
            metadata: { name, description }
          });
        } catch (error) {
          console.error("Error starting sequence:", error);
          toast({
            title: "Warning",
            description: "Sequence created but failed to start. Please try starting it manually.",
            variant: "destructive",
          });
        }
      }

      toast({
        title: "Success",
        description: "Sequence saved successfully",
      });

      return sequence;
    } catch (error) {
      console.error("Error in saveSequence:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save sequence. Please try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    saveSequence,
    handleDelete,
    isSaving
  };
};